import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SeoBreadcrumb } from "@/components/SeoBreadcrumb";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Award, Heart, Users, Tag } from "lucide-react";
import { getLatestArticles } from "@/data/articles";

import maestroOh from "@/assets/maestro-oh.jpg";
import maestroRicci from "@/assets/maestro-ricci.jpg";
import maestroNasri from "@/assets/maestro-nasri.jpg";
import maestroSilvio from "@/assets/maestro-silvio.jpg";
import maestroValerio from "@/assets/maestro-valerio.jpg";

const maestri = [
  {
    name: "Massimiliano Ricci",
    role: "Direttore Tecnico",
    dan: "V Dan",
    image: maestroRicci,
    bio: "Istruttore federale di secondo livello con laurea in Scienze Motorie. Ex atleta agonista, terzo classificato ai Campionati Italiani Assoluti 2008. Oltre 10 anni di esperienza nell'insegnamento del Taekwondo.",
    specialties: ["Istruttore federale II livello", "Laurea Scienze Motorie", "3° Campionati Italiani 2008", "10+ anni insegnamento"]
  },
  {
    name: "Oh Eun Jae",
    role: "Istruttore",
    dan: "V Dan",
    image: maestroOh,
    bio: "Istruttore federale di secondo livello con laurea in Scienze Motorie, specializzato in preparazione atletica per sport da combattimento. Docente nella scuola secondaria di secondo grado, ex atleta agonista con 27 anni di esperienza nel Taekwondo.",
    specialties: ["Istruttore federale II livello", "Laurea Scienze Motorie", "Docente scuola superiore", "27 anni di esperienza"]
  },
  {
    name: "Youmna Nasri",
    role: "Allenatrice",
    dan: "III Dan",
    image: maestroNasri,
    bio: "Allenatrice federale di primo livello, plurimedagliata ai Campionati Italiani junior e senior. Ha rappresentato l'Italia in competizioni internazionali. Esperienza diretta nel lavoro con bambini e giovani atleti.",
    specialties: ["Allenatrice federale I livello", "Plurimedagliata nazionale", "Competizioni internazionali", "Lavoro con bambini e giovani"]
  },
  {
    name: "Valerio Venanzi",
    role: "Allenatore",
    dan: "IV Dan",
    image: maestroValerio,
    bio: "Insegnante di primo livello federale. Medaglia d'argento ai Campionati Italiani Junior 2016. Dopo circa 10 anni di agonismo è passato all'attività di insegnamento, che porta avanti con grande soddisfazione nel vedere crescere i giovanissimi.",
    specialties: ["Insegnante federale I livello", "IV Dan", "Argento Campionati Italiani Junior 2016", "10 anni di agonismo"]
  },
  {
    name: "Silvio Bencivenga",
    role: "Allenatore",
    dan: "III Dan",
    image: maestroSilvio,
    bio: "Allenatore di primo livello federale, ex atleta agonista con partecipazioni a gare nazionali e internazionali. Oggi mette la sua esperienza al servizio dei più giovani, trasmettendo passione e valori del Taekwondo con dedizione e entusiasmo.",
    specialties: ["Allenatore federale I livello", "Ex atleta agonista", "Gare nazionali e internazionali"]
  }
];

const altriMaestri: { name: string; image: string }[] = [];
const categoryLabels: Record<string, string> = {
  bambini: "Bambini", ragazzi: "Ragazzi", adulti: "Adulti",
  generale: "Generale", zona: "Zona", agonisti: "Agonisti"
};

const Maestri = () => {
  const blogArticles = getLatestArticles(3);
  return (
    <Layout>
      <Helmet>
        <title>I Maestri - Taekwondo Mattei Roma</title>
        <meta name="description" content="Conosci i maestri di Taekwondo Mattei: istruttori federali qualificati, ex atleti e educatori con anni di esperienza." />
        <link rel="canonical" href="https://taekwondomattei.com/maestri" />
        <meta property="og:title" content="I Maestri - Taekwondo Mattei Roma" />
        <meta property="og:description" content="Conosci i maestri di Taekwondo Mattei: istruttori federali qualificati, ex atleti e educatori con anni di esperienza." />
        <meta property="og:image" content="https://taekwondomattei.com/og-maestri.jpg" />
        <meta property="og:url" content="https://taekwondomattei.com/maestri" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="I Maestri - Taekwondo Mattei Roma" />
        <meta name="twitter:description" content="Conosci i maestri di Taekwondo Mattei: istruttori federali qualificati, ex atleti e educatori con anni di esperienza." />
        <meta name="twitter:image" content="https://taekwondomattei.com/og-maestri.jpg" />
      </Helmet>
      <SeoBreadcrumb items={[{ label: "I Maestri" }]} />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary">
        <div className="container-wide mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              I Maestri
            </h1>
            <p className="font-body text-xl text-muted-foreground">
              Insegnanti qualificati, atleti esperti, educatori attenti. 
              Persone che hanno fatto del Taekwondo la loro passione e la loro missione.
            </p>
          </div>
        </div>
      </section>

      {/* Maestri profiles */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <div className="space-y-24">
            {maestri.map((maestro, index) => (
              <div
                key={maestro.name}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start`}
              >
                <div className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                  <div className="relative overflow-hidden rounded-2xl max-w-md mx-auto">
                    <img
                      src={maestro.image}
                      alt={maestro.name}
                      className="shadow-large w-full"
                      loading="lazy"
                    />
                    <div className="absolute bottom-3 right-3 px-4 py-2 bg-black/70 backdrop-blur-sm rounded-lg border border-gold/40">
                      <span className="font-display text-base font-bold text-gold">
                        {maestro.dan}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`space-y-6 ${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
                  <div>
                    <span className="font-body text-sm font-medium text-gold uppercase tracking-wider">
                      {maestro.role}
                    </span>
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mt-2">
                      {maestro.name}
                    </h2>
                  </div>

                  <p className="font-body text-lg text-muted-foreground leading-relaxed">
                    {maestro.bio}
                  </p>

                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-3">
                      Qualifiche e risultati
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {maestro.specialties.map((specialty) => (
                        <span
                          key={specialty}
                          className="px-4 py-2 bg-secondary rounded-full font-body text-sm text-foreground border border-gold/20"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {altriMaestri.length > 0 && (
            <div className="mt-24">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
                Il nostro team
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-lg mx-auto">
                {altriMaestri.map((maestro) => (
                  <div key={maestro.name} className="text-center">
                    <img
                      src={maestro.image}
                      alt={maestro.name}
                      className="w-48 h-48 rounded-2xl object-cover mx-auto shadow-large mb-4"
                    />
                    <h3 className="font-display text-xl font-semibold text-foreground">
                      {maestro.name}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-secondary">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="I nostri valori"
            subtitle="Cosa ci guida nell'insegnamento"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Competenza",
                description: "Formazione continua, certificazioni federali e esperienza sul campo. I nostri maestri si aggiornano costantemente."
              },
              {
                icon: Heart,
                title: "Passione",
                description: "Insegnare non è un lavoro, è una vocazione. Ogni lezione è preparata con cura e dedizione."
              },
              {
                icon: Users,
                title: "Attenzione",
                description: "Ogni allievo è unico. Ascoltiamo le esigenze individuali e adattiamo l'approccio di conseguenza."
              }
            ].map((value) => (
              <div key={value.title} className="card-elevated p-8 text-center border border-border">
                <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="h-8 w-8 text-gold" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-4">
                  {value.title}
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articoli correlati */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Dal nostro blog"
            subtitle="Articoli su formazione, crescita e Taekwondo"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogArticles.map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group card-elevated border border-border p-6"
              >
                <span className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                  {categoryLabels[article.category] || article.category}
                </span>
                <h3 className="font-display text-lg font-semibold text-foreground mt-4 group-hover:text-gold transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="font-body text-sm text-muted-foreground mt-2 line-clamp-2">
                  {article.excerpt}
                </p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog" className="font-body text-gold hover:text-gold-light transition-colors inline-flex items-center gap-2">
              Tutti gli articoli <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Cross-link */}
      <section className="section-padding bg-secondary">
        <div className="container-wide mx-auto">
          <h2 className="sr-only">Scopri anche</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/percorsi" className="group card-elevated border border-border p-8 hover:border-gold/30 transition-colors">
              <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-gold transition-colors mb-2">
                I Percorsi →
              </h3>
              <p className="font-body text-muted-foreground">
                Scopri i programmi per bambini, ragazzi, adulti e agonisti.
              </p>
            </Link>
            <Link to="/academy" className="group card-elevated border border-border p-8 hover:border-gold/30 transition-colors">
              <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-gold transition-colors mb-2">
                L'Academy →
              </h3>
              <p className="font-body text-muted-foreground">
                La nostra storia, il metodo e l'approccio educativo.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding gradient-black">
        <div className="container-narrow mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gold mb-6">
            Conosci i nostri maestri
          </h2>
          <p className="font-body text-lg text-gold/70 mb-8 max-w-xl mx-auto">
            Vieni a trovarci per una lezione di prova e scopri di persona 
            il nostro approccio all'insegnamento.
          </p>
          <Link to="/lezione-di-prova" className="btn-accent text-lg">
            Prenota una lezione di prova
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Maestri;
